import { Button, InputField, PaginationTable, TableWrapper } from 'components/Global';
import { ReactComponent as PeopleIcon } from 'icons/people-filled-icon.svg';
import { ReactComponent as ArrowLeft } from 'icons/arrow-left.svg';
import { ReactComponent as PointsIcon } from 'icons/points-icon.svg';
import { useAppContext } from 'components/Context/AppContext';
import { checkEmptyObject, checkPermission, phoneWithCode, setCurrency } from 'utils/functionality';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { changeUserStatus, fetchAllUsers, fetchCustomerInfo } from 'features/user/actions';
import { getUserLoyaltyExperience, getUserLoyaltyHistory } from 'features/dashboard-point/actions';
import { format } from 'date-fns';
import clsx from 'clsx';
import { getReferralPointUser } from 'features/referral-point/actions';
import { trackEvent } from 'utils/analytics';
import { useFliptBoolean } from '@flipt-io/flipt-client-react';
import { getConfig } from 'config/runtime';

const fallbackImg = '/assets/image/ktp-fallback.png';

const IMAGE_BASE_URL = getConfig('REACT_APP_IMAGES');

const CustomerDetail = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const data = useSelector((state) => state.user?.singleUser);
  const { offCanvasMenu, currentMenu } = useSelector((state) => state.menu);
  const { name, email, phone, wa_number, phone_code, PersonalInfos, referral } = data;

  const { data: userLoyaltyExperienceData } = useSelector((state) => state.userLoyaltyExperience);
  const { data: userLoyaltyHistoryData } = useSelector((state) => state.userLoyaltyHistory);
  const { data: referralPointUserData } = useSelector((state) => state.referralPoint);

  const { setShowBlockConfirmation, showToast } = useAppContext();

  const [currentPage, setCurrentPage] = useState(1);
  const [isUserBlocked, setIsUserBlocked] = useState(false);
  const [userStatus, setUserStatus] = useState('');

  const isUploadNewDocumentEnabled = useFliptBoolean('FLAG_FE_NEW_UPLOAD_DOCUMENT', false, 'anonymous');

  const params = useParams();

  const blockUserHandler = () => {
    setIsUserBlocked((prev) => !prev);
  };

  const saveHandler = () => {
    setShowBlockConfirmation({
      show: true,
      message: !isUserBlocked
        ? 'Apakah anda yakin ingin mengaktifkan user kembali?'
        : 'Apakah anda yakin ingin Memblokir User?',
      onClick: async () => {
        try {
          trackEvent('customer_status_changed', {
            customer_id: params?.id,
            to_status: userStatus,
            source: 'customer_detail',
          });
          await dispatch(changeUserStatus({ customerId: params.id, payload: { status: userStatus } })).unwrap();
          showToast({ type: 'success', message: 'Berhasil Update User' });
          navigate('/user-customer');
          dispatch(fetchAllUsers());
        } catch (error) {
          showToast({ type: 'error', message: 'Gagal Update User' });
        } finally {
          setShowBlockConfirmation(false);
        }
      },
    });
  };

  useEffect(() => {
    if (!params?.id) return;
    dispatch(fetchCustomerInfo(params.id));

    dispatch(getUserLoyaltyExperience({ page: 1, limit: 10, userId: params.id }));

    dispatch(getUserLoyaltyHistory({ page: 1, limit: 10, userId: params.id }));

    dispatch(getReferralPointUser(params.id));
  }, [params]);

  useEffect(() => {
    if (checkEmptyObject(data)) return;
    setIsUserBlocked(() => (data.status == 'approved' || data.status == 'new' ? false : true));
    setUserStatus(data.status);
  }, [data]);

  useEffect(() => {
    setUserStatus(isUserBlocked ? 'blocked' : 'approved');
  }, [isUserBlocked]);

  return (
    <div className="user__detail">
      <button className="user__back-btn" onClick={() => navigate(-1)}>
        <ArrowLeft />
        <span>Kembali</span>
      </button>

      <div className="user__header">
        <div className="user__status-wrapper">
          <div className="user__status-info">
            <p>Status</p>
            {isUserBlocked ? (
              <span className="user__status-badge user__status-badge--blocked">Terblokir</span>
            ) : (
              <span className="user__status-badge user__status-badge--active">Aktif</span>
            )}
          </div>

          <div className="user__toggle-status">
            <label className="switch">
              <input type="checkbox" onChange={blockUserHandler} role={'switch'} checked={!isUserBlocked} />
              <span className="slider"></span>
            </label>
          </div>
        </div>
      </div>

      <div className="user__data">
        <div className="user__form-data">
          <InputField label="Name Lengkap" type="text" name="name" value={name || ''} disable />
          <InputField label="Email" type="email" name="email" value={email || ''} disable />
          <InputField
            label="No. Handphone"
            type="text"
            name="phone"
            value={phoneWithCode(phone_code, phone) || ''}
            disable
          />
          <InputField
            label="Whatsapp"
            type="text"
            name="whatsapp"
            value={phoneWithCode(phone_code, wa_number) || ''}
            disable
          />
        </div>

        <div className="user__form-data">
          <InputField label="Kode Referral" type="text" name="referral" value={referral || ''} disable />
          <InputField
            label="Loyalty Level"
            type="text"
            name="loyalty"
            value={
              !checkEmptyObject(userLoyaltyExperienceData) && userLoyaltyExperienceData?.data?.length !== 0
                ? userLoyaltyExperienceData?.data[0]?.level_name
                : '-'
            }
            disable
          />
          <InputField
            icon={<PointsIcon />}
            iconPosition="start"
            label="Jumlah Point"
            type="text"
            name="point"
            value={
              !checkEmptyObject(referralPointUserData)
                ? setCurrency(referralPointUserData?.point).replace('Rp', '')
                : '-'
            }
            disable
          />
        </div>
      </div>

      <div className="user__identity">
        <TableWrapper title="Foto Identitas Diri" icon={<PeopleIcon fill="#FF9900" width="25px" height="25px" />}>
          <div className="user__image-data">
            <div className="user__identity user__identity--id">
              <p className="user__identity-subtitle">Foto KTP</p>
              <div className="user__identity-image-wrapper">
                <img
                  src={
                    PersonalInfos?.ktp
                      ? isUploadNewDocumentEnabled
                        ? PersonalInfos?.ktp
                        : `${IMAGE_BASE_URL}${PersonalInfos?.ktp}`
                      : fallbackImg
                  }
                  alt="foto-ktp"
                />
              </div>
            </div>

            <div className="user__identity user__identity--license">
              <p className="user__identity-subtitle">Foto SIM</p>
              <div className="user__identity-image-wrapper">
                <img
                  src={
                    PersonalInfos?.sim
                      ? isUploadNewDocumentEnabled
                        ? PersonalInfos?.sim
                        : `${IMAGE_BASE_URL}${PersonalInfos?.sim}`
                      : fallbackImg
                  }
                  alt="foto-sim"
                />
              </div>
            </div>
          </div>
        </TableWrapper>
      </div>

      <div className="user__history-transaction-point">
        <h2>History Transaksi</h2>
        <ul className="user__history-transaction-point__transaction">
          {checkEmptyObject(userLoyaltyHistoryData) ||
          !userLoyaltyHistoryData.data ||
          userLoyaltyHistoryData.data.length === 0 ? (
            <p className="empty-data">Belum ada Transaksi</p>
          ) : (
            userLoyaltyHistoryData.data.map((item) => (
              <li key={item.id}>
                <div>
                  <p>{item.wording}</p>
                  <p>
                    {format(new Date(item.date), 'dd MMMM yyyy')} {format(new Date(item.date), 'HH:mm:ss')}
                  </p>
                </div>
                <p className={clsx('point', item.value_type === 'minus' ? 'minus' : 'plus')}>
                  {item.value_type === 'minus'
                    ? `- ${setCurrency(item.point).replace('Rp', '')}`
                    : `+ ${setCurrency(item.point).replace('Rp', '')}`}
                </p>
              </li>
            ))
          )}
        </ul>
        <PaginationTable
          currentPage={currentPage}
          onPageChange={(newPage) => setCurrentPage(newPage)}
          totalCount={userLoyaltyHistoryData?.pagination?.total}
        />
      </div>

      {checkPermission(offCanvasMenu, currentMenu, 'update') && (
        <div className="user__save-btn">
          <Button width={208} size="sm" onClick={saveHandler} disabled={userStatus === data?.status}>
            Simpan
          </Button>
        </div>
      )}
    </div>
  );
};

export default CustomerDetail;
